import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatIDR, formatDateID, formatTimeID } from "@/lib/format";
import { toast } from "sonner";
import { ArrowDownCircle, ArrowUpCircle, Calendar, ChevronDown, ChevronLeft, Clock, Wallet } from "lucide-react";

const BLUE = "#2563EB";
const GRAD = "linear-gradient(135deg, #1D4ED8 0%, #2563EB 55%, #3B82F6 100%)";
const cardM = "bg-white rounded-2xl border border-slate-100 shadow-sm";
const inpM = "h-10 px-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400";

const FILTERS = [
  { id: "harian", label: "Harian" },
  { id: "mingguan", label: "Mingguan" },
  { id: "bulanan", label: "Bulanan" },
  { id: "tahunan", label: "Tahunan" },
];
const MUT_LABEL = {
  gaji_harian: "Gaji harian · Absen pulang",
  potongan_terlambat: "Potongan keterlambatan",
  lembur: "Upah lembur disetujui",
  bonus: "Bonus",
  tunjangan: "Tunjangan",
  koreksi: "Penyesuaian",
  pencairan: "Pencairan saldo",
};
const signed = (n) => `${n < 0 ? "−" : "+"}${formatIDR(Math.abs(n))}`;

function DayLabel({ iso }) {
  try {
    return new Date(iso + "T00:00:00+07:00").toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "Asia/Jakarta" });
  } catch { return iso; }
}

function SaldoCard({ saldo, masuk, keluar, compact }) {
  return (
    <div className="rounded-3xl p-5 text-white relative overflow-hidden" style={{ background: GRAD }} data-testid="saldo-card">
      <div className="absolute -right-10 -top-10 w-44 h-44 rounded-full bg-white/10" />
      <div className="absolute -right-4 top-14 w-28 h-28 rounded-full bg-white/10" />
      <div className="flex items-center gap-3 relative">
        <span className="w-11 h-11 rounded-2xl bg-white/15 flex items-center justify-center shrink-0"><Wallet className="w-6 h-6" /></span>
        <div className="w-px h-10 bg-white/25" />
        <div className="min-w-0">
          <div className="text-blue-100 text-[12px]">Saldo tersedia</div>
          <div className="font-extrabold text-[26px] leading-tight" data-testid="saldo-tersedia">{formatIDR(saldo)}</div>
        </div>
      </div>
      <div className="mt-4 pt-3 border-t border-white/20 grid grid-cols-2 relative">
        <div className="flex items-center gap-1.5">
          <ArrowDownCircle className="w-4 h-4 text-blue-100" />
          <span className="text-[11px] text-blue-100">Masuk</span>
          <span className="text-sm font-bold" data-testid="saldo-masuk">+{formatIDR(masuk)}</span>
        </div>
        <div className="flex items-center gap-1.5 pl-3 border-l border-white/20">
          <ArrowUpCircle className="w-4 h-4 text-blue-100" />
          <span className="text-[11px] text-blue-100">Keluar</span>
          <span className="text-sm font-bold" data-testid="saldo-keluar">−{formatIDR(keluar)}</span>
        </div>
      </div>
    </div>
  );
}

function MutationList({ data }) {
  const groups = {};
  (data?.items || []).forEach((m) => {
    const d = m.work_date || "";
    (groups[d] = groups[d] || []).push(m);
  });
  const days = Object.keys(groups).sort((a, b) => b.localeCompare(a));
  if (!days.length) {
    return <div className={`${cardM} py-10 text-center text-xs text-slate-400`} data-testid="mutasi-empty">Belum ada mutasi pada rentang ini.</div>;
  }
  return (
    <div className="space-y-3">
      {days.map((d) => (
        <div key={d} className={`${cardM} p-4`} data-testid={`mutasi-day-${d}`}>
          <div className="font-bold text-slate-900 text-sm"><DayLabel iso={d} /></div>
          <div className="divide-y divide-slate-50 mt-2">
            {groups[d].map((m) => {
              const masuk = m.direction === "masuk";
              return (
                <div key={m.id} className="py-3 flex items-start gap-3" data-testid={`mutasi-item-${m.id}`}>
                  <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${masuk ? "bg-emerald-50 text-emerald-500" : "bg-red-50 text-red-500"}`}>
                    {masuk ? <ArrowDownCircle className="w-5 h-5" /> : <ArrowUpCircle className="w-5 h-5" />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-800 leading-tight">{m.note || MUT_LABEL[m.kind] || m.kind}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5">{formatTimeID(m.created_at)}</div>
                    <div className="text-[11px] text-slate-400">Saldo setelah transaksi {formatIDR(m.balance_after)}</div>
                  </div>
                  <div className={`text-sm font-bold whitespace-nowrap ${masuk ? "text-emerald-500" : "text-red-500"}`} data-testid={`mutasi-amount-${m.id}`}>
                    {signed(m.signed_amount)}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="pt-2 mt-1 border-t border-slate-100 text-[11px] text-slate-400">
            {/* groups[d] urut terbaru→terlama; saldo akhir hari = balance_after item teratas,
                saldo awal hari = saldo akhir hari − total mutasi hari itu */}
            Saldo awal hari {formatIDR(groups[d][0]?.balance_after - groups[d].reduce((a, m) => a + m.signed_amount, 0))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ==================== HALAMAN RINGKASAN SALDO ==================== */
export function SaldoScreen({ onBack, onOpenMutasi }) {
  const [data, setData] = useState(null);
  const [muts, setMuts] = useState(null);
  const load = useCallback(() => {
    api.get("/saldo/me").then((r) => setData(r.data)).catch(() => toast.error("Gagal memuat saldo"));
    api.get("/saldo/me/mutations", { params: { mode: "harian" } }).then((r) => setMuts(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const t = data?.today;
  const p = data?.pending;
  return (
    <div data-testid="screen-saldo">
      <SubHeaderLocal title="Saldo" onBack={onBack} testid="saldo-header" />
      <div className="px-4 space-y-3.5 pb-6">
        <button data-testid="saldo-card-btn" onClick={onOpenMutasi} className="w-full text-left active:scale-[0.99] transition-transform">
          <SaldoCard saldo={data?.saldo || 0} masuk={t ? t.gaji + t.lembur + t.lainnya : 0} keluar={t ? t.terlambat + t.lainnya_keluar : 0} />
          <div className="text-center text-[10px] text-slate-400 mt-1.5">Ketuk kartu untuk melihat Riwayat Mutasi</div>
        </button>

        <div className={`${cardM} p-4`} data-testid="saldo-today">
          <div className="text-xs font-bold text-slate-900 mb-2.5">Pendapatan Hari Ini</div>
          {p ? (
            <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2.5 mb-2.5" data-testid="saldo-pending">
              <Clock className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
              <div className="text-[11px] text-amber-700">
                <span className="font-bold">Menunggu absen pulang</span> — estimasi masuk saldo {formatIDR(p.estimasi_net)}
                                {p.potongan_terlambat > 0 && ` (sudah termasuk potongan terlambat ${formatIDR(p.potongan_terlambat)})`}.
                Nominal perkiraan belum termasuk saldo tersedia.
              </div>
            </div>
          ) : null}
          <div className="space-y-1.5 text-xs">
            <Row label="Gaji harian" value={t?.gaji || 0} plus testid="today-gaji" pending={p && !t?.gaji ? p.gaji : null} />
            <Row label="Potongan keterlambatan" value={t?.terlambat || 0} minus testid="today-terlambat" pending={p && !t?.terlambat ? p.potongan_terlambat : null} />
            <Row label="Upah lembur" value={t?.lembur || 0} plus testid="today-lembur" />
            <Row label="Bonus / tunjangan / lainnya" value={(t?.lainnya || 0) - (t?.lainnya_keluar || 0)} plus testid="today-lainnya" />
            <div className="flex justify-between items-center pt-2 mt-1 border-t border-slate-100">
              <span className="font-bold text-slate-800 text-sm">Total pendapatan bersih</span>
              <span className="font-extrabold text-slate-900 text-base" data-testid="today-net">
                {p && !(t?.gaji) ? <span className="text-amber-600 text-xs font-bold">Menunggu absen pulang</span> : formatIDR(t?.net || 0)}
              </span>
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] font-semibold text-slate-400">Mutasi hari ini</div>
            <button data-testid="saldo-lihat-semua" onClick={onOpenMutasi} className="text-[11px] font-bold text-blue-600">Lihat semua</button>
          </div>
          <MutationList data={muts} />
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, plus, minus, testid, pending }) {
  const neg = value < 0;
  const cls = (minus || neg) ? "text-red-500" : "text-emerald-600";
  const shown = `${neg ? "−" : minus ? "−" : "+"}${formatIDR(Math.abs(value))}`;
  return (
    <div className="flex justify-between items-center">
      <span className="text-slate-500">{label}</span>
      {pending != null && !value ? (
        <span className="text-[11px] text-amber-600 font-semibold" data-testid={testid}>±{formatIDR(pending)} (perkiraan)</span>
      ) : (
        <span className={`font-semibold ${value ? cls : "text-slate-400"}`} data-testid={testid}>{value ? shown : "—"}</span>
      )}
    </div>
  );
}

/* ==================== HALAMAN RIWAYAT MUTASI ==================== */
export function MutasiScreen({ onBack }) {
  const [mode, setMode] = useState("harian");
  const [rangeOpen, setRangeOpen] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback((m, f, t) => {
    setLoading(true);
    const params = m === "rentang" ? { mode: m, date_from: f, date_to: t } : { mode: m };
    api.get("/saldo/me/mutations", { params })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Gagal memuat mutasi"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(mode, from, to); }, [mode]);

  const applyRange = () => {
    if (!from || !to) return toast.error("Pilih tanggal mulai dan selesai");
    setMode("rentang");
    setRangeOpen(false);
    load("rentang", from, to);
  };

  return (
    <div data-testid="screen-mutasi">
      <div className="flex items-center gap-2 px-4 pt-4 pb-3 sticky top-0 z-30" style={{ background: "#F8FAFC" }} data-testid="mutasi-header">
        <button data-testid="mutasi-header-back" onClick={onBack} className="w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-600 shrink-0"><ChevronLeft className="w-5 h-5" /></button>
        <h1 className="flex-1 text-center font-bold text-slate-900 text-base pr-9">Mutasi Saldo</h1>
      </div>
      <div className="px-4 space-y-3.5 pb-6">
        {/* Filter pill */}
        <div className="grid grid-cols-4 gap-2" data-testid="mutasi-filters">
          {FILTERS.map((f) => (
            <button key={f.id} data-testid={`filter-${f.id}`} onClick={() => { setMode(f.id); setRangeOpen(false); }}
              className={`h-10 rounded-xl text-[12px] font-bold transition-colors ${mode === f.id ? "text-white" : "bg-white text-slate-500 border border-slate-200"}`}
              style={mode === f.id ? { background: BLUE } : {}}>{f.label}</button>
          ))}
        </div>
        {/* Rentang tanggal */}
        <button data-testid="mutasi-range-toggle" onClick={() => setRangeOpen((s) => !s)}
          className={`${cardM} w-full px-4 py-3 flex items-center gap-2.5 text-left`}>
          <Calendar className="w-4 h-4 text-blue-600 shrink-0" />
          <span className="flex-1 text-[13px] font-semibold text-slate-700" data-testid="mutasi-range-label">
            {formatDateID(data?.date_from)} – {formatDateID(data?.date_to)}
          </span>
          <ChevronDown className={`w-4 h-4 text-blue-600 transition-transform ${rangeOpen ? "rotate-180" : ""}`} />
        </button>
        {rangeOpen && (
          <div className={`${cardM} p-4 space-y-3`} data-testid="mutasi-range-form">
            <div className="grid grid-cols-2 gap-3">
              <div><div className="text-[11px] font-semibold text-slate-500 mb-1">Dari</div>
                <input data-testid="range-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={`${inpM} w-full`} /></div>
              <div><div className="text-[11px] font-semibold text-slate-500 mb-1">Sampai</div>
                <input data-testid="range-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className={`${inpM} w-full`} /></div>
            </div>
            <button data-testid="range-apply" onClick={applyRange} className="w-full h-11 rounded-xl text-white font-bold text-sm" style={{ background: BLUE }}>Terapkan Rentang</button>
          </div>
        )}

        <SaldoCard saldo={data?.closing_balance ?? 0} masuk={data?.masuk ?? 0} keluar={data?.keluar ?? 0} />
        <div className={`${cardM} px-4 py-3 flex justify-between items-center`} data-testid="mutasi-net">
          <span className="text-[13px] font-semibold text-slate-600">Pendapatan bersih {mode === "harian" ? "hari ini" : "rentang ini"}</span>
          <span className="font-extrabold text-slate-900">{formatIDR((data?.masuk ?? 0) - (data?.keluar ?? 0))}</span>
        </div>

        <div>
          <div className="text-base font-extrabold text-slate-900 mb-1">Riwayat Mutasi</div>
          {loading ? <div className={`${cardM} py-10 text-center text-xs text-slate-400`}>Memuat...</div> : <MutationList data={data} />}
        </div>
        {data && <div className="text-center text-[11px] text-slate-400" data-testid="mutasi-opening">Saldo awal {formatIDR(data.opening_balance)}</div>}
      </div>
    </div>
  );
}

function SubHeaderLocal({ title, onBack, testid }) {
  return (
    <div className="flex items-center gap-2 px-4 pt-4 pb-3 sticky top-0 z-30" style={{ background: "#F8FAFC" }} data-testid={testid}>
      <button data-testid={`${testid}-back`} onClick={onBack} className="w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-600 shrink-0"><ChevronLeft className="w-5 h-5" /></button>
      <h1 className="flex-1 text-center font-bold text-slate-900 text-base pr-9">{title}</h1>
    </div>
  );
}
